/**
 * App.tsx — SysOps Panel root.
 *
 * Auth guard wraps the entire app via <RoleGate>. Inside the gate, a
 * BrowserRouter + nested route tree renders the ShellLayout (topbar +
 * outlet) and per-page modules.
 */

import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
} from 'react-router';

import { RoleGate } from '@/components/RoleGate';
import { ShellLayout } from '@/components/ShellLayout';

import { SysOpsHome } from '@/modules/Home/SysOpsHome';
import { AboutPage } from '@/modules/About/AboutPage';
import { KillListDashboard } from '@/modules/KillList/KillListDashboard';
import { KillListEntryDetail } from '@/modules/KillList/KillListEntryDetail';
import { IncidentsShell } from '@/modules/Incidents/IncidentsShell';
import { PipelineErrorsTable } from '@/modules/Incidents/PipelineErrorsTable';
import { NotificationFailuresTable } from '@/modules/Incidents/NotificationFailuresTable';
import { AwaitingSignaturesStuckTable } from '@/modules/Incidents/AwaitingSignaturesStuckTable';
import { AuditShell } from '@/modules/Audit/AuditShell';
import { PurgeHistoryViewer } from '@/modules/Audit/PurgeHistoryViewer';
import { TestModeActivityViewer } from '@/modules/Audit/TestModeActivityViewer';
import { NotificationsHistoryViewer } from '@/modules/Audit/NotificationsHistoryViewer';
import { DataMgmtHome } from '@/modules/DataManagement/DataMgmtHome';
import { OfacRubricRunner } from '@/modules/DataManagement/OfacRubricRunner';
import { MissingOfficerCodeQueue } from '@/modules/Identity/MissingOfficerCodeQueue';
import { CdNameMismatchQueue } from '@/modules/Identity/CdNameMismatchQueue';
import { OperatorCatalogIssuesQueue } from '@/modules/Identity/OperatorCatalogIssuesQueue';
import { InsiderScanPage } from '@/modules/Identity/InsiderScanPage';

export function App(): JSX.Element {
  return (
    <RoleGate>
      {/* Root-mount basename — dedicated pcb-sysops-panel SWA. Legacy
          '/sys-ops' sub-path retired 2026-05-10 (see memory
          feedback_swa_deploy_target_unbreakable_rule_20260510). */}
      <BrowserRouter basename="/">
        <Routes>
          <Route element={<ShellLayout />}>
            <Route index element={<SysOpsHome />} />
            <Route path="about" element={<AboutPage />} />
            <Route path="kill-list" element={<KillListDashboard />} />
            <Route path="kill-list/:entryId" element={<KillListEntryDetail />} />

            <Route path="incidents" element={<IncidentsShell />}>
              <Route index element={<Navigate to="pipeline-errors" replace />} />
              <Route path="pipeline-errors" element={<PipelineErrorsTable />} />
              <Route path="notification-failures" element={<NotificationFailuresTable />} />
              <Route path="awaiting-signatures-stuck" element={<AwaitingSignaturesStuckTable />} />
            </Route>

            <Route path="audit" element={<AuditShell />}>
              <Route index element={<Navigate to="purge-history" replace />} />
              <Route path="purge-history" element={<PurgeHistoryViewer />} />
              <Route path="test-mode-activity" element={<TestModeActivityViewer />} />
              <Route path="notifications-history" element={<NotificationsHistoryViewer />} />
            </Route>

            <Route path="data-mgmt" element={<DataMgmtHome />} />
            {/* OFAC Rubric Runner relocated from ROP-teams Admin 2026-06-18
                (BACKLOG-0579). Dev diagnostic — server enforces SuperAdmin +
                dev.tools.enabled; the Home tile is hidden unless the flag is on. */}
            <Route path="data-mgmt/ofac-rubric" element={<OfacRubricRunner />} />

            {/* Domain 1.7 (Bill 2026-05-14) — MissingOfficerCode resolve queue. */}
            <Route path="identity/missing-officer-code" element={<MissingOfficerCodeQueue />} />
            {/* v0.8.5 (Bill 2026-05-15) — CD DisplayName mismatch queue. */}
            <Route path="identity/cd-name-mismatch" element={<CdNameMismatchQueue />} />
            {/* v0.9.3 (Bill 2026-05-15) — Operator Catalog Issues queue. */}
            <Route path="identity/operator-catalog-issues" element={<OperatorCatalogIssuesQueue />} />
            {/* Insider scan (Bill 2026-10-28): who the contact-change check covers. */}
            <Route path="identity/insider-scan" element={<InsiderScanPage />} />

            {/* MSAL redirectUri is the panel root, so we don't need a
                dedicated callback route — the in-flight MSAL redirect is
                finalized by ensureMsalReady() on every page load. */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </RoleGate>
  );
}
